import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Trash2, Plus, Minus, Repeat, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";

export default function CartPage() {
  const {
    items,
    removeItem,
    updateQuantity,
    subtotal,
    hasSubscription,
    checkout,
    checkingOut,
    checkoutUrl,
  } = useCart();

  return (
    <div className="min-h-screen bg-parchment" data-testid="cart-page">
      <section className="bg-royal-blue text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="heading-hero">Your Cart</h1>
          <p className="text-slate-300 mt-2">Review your items before secure checkout.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-4">Your cart is empty.</p>
            <Link to="/shop">
              <Button className="btn-gold" data-testid="cart-page-shop-btn">Browse the Shop</Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div
                  key={item.lineId}
                  className="flex gap-4 bg-white rounded-lg border border-slate-200 p-4"
                  data-testid={`cart-page-item-${item.internal_id}`}
                >
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-16 h-16 rounded object-cover bg-slate-100 flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-kingdom-gold/10 flex items-center justify-center flex-shrink-0">
                      {item.type === "subscription" ? <Repeat className="w-6 h-6 text-kingdom-gold" /> : <ShoppingCart className="w-6 h-6 text-kingdom-gold" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-royal-blue">{item.name}</p>
                    {item.type === "subscription" && (
                      <span className="text-xs text-royal-purple font-medium">Monthly subscription</span>
                    )}
                    <p className="font-bold text-slate-700 mt-1">
                      ${Number(item.price).toFixed(2)}
                      {item.type === "subscription" && <span className="text-xs text-slate-400">/mo</span>}
                    </p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeItem(item.lineId)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      data-testid={`cart-page-remove-${item.internal_id}`}
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {item.type !== "subscription" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                          className="w-7 h-7 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40"
                          disabled={item.quantity <= 1}
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm w-5 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                          className="w-7 h-7 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-royal-blue hover:underline">
                <ArrowLeft className="w-4 h-4" /> Continue shopping
              </Link>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-24">
                <h2 className="font-heading text-xl text-royal-blue mb-4">Order Summary</h2>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Subtotal</span>
                  <span className="font-bold text-royal-blue" data-testid="cart-page-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {hasSubscription && (
                  <p className="text-xs text-slate-500 mb-3">
                    Includes a monthly subscription billed each month until canceled.
                  </p>
                )}
                <Button
                  className="w-full btn-gold mt-3"
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="cart-page-checkout-btn"
                >
                  {checkingOut ? "Redirecting…" : "Checkout Securely"}
                </Button>
                {checkoutUrl && (
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center text-sm font-semibold text-royal-blue underline mt-3"
                    data-testid="cart-page-stripe-fallback-link"
                  >
                    Open secure Stripe checkout
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
